import URI from "urijs";
import { Location } from "history";
import QueryString from "query-string";
import getUrl from "./getUrl";

const defaultDocPage = "index.html";

export enum Product {
  qmlativ = "Qmlativ",
  sms = "SMS 2.0",
}

export interface IDocLocation {
  id: string;
  product: Product;
  href: string;
  area: string;
  api: string;
  remoteUrl: string;
  isTryItOut: boolean;
  isDefault: boolean;
}

export interface ILocation {
  path: string;
  tab: string;
  doc?: IDocLocation;
}

const getDocUrl = (search: string): string => {
  let { id } = QueryString.parse(search);
  if (!id || (!Array.isArray(id) && id.toLowerCase() === defaultDocPage)) {
    return defaultDocPage;
  }
  return id.toString().toLowerCase();
};

function getDocsInfo<TSearchParams>(location: Location<TSearchParams>): IDocLocation {
  const { search } = location;

  const id = getDocUrl(search);
  const name = encodeURIComponent(id);
  const url = getUrl(`/docfx?name=${name}`);

  let paths = id.split("/");
  let repo = paths[0].toLowerCase();
  const product = repo.startsWith("qmlativ") ? Product.qmlativ : Product.sms;
  let area = "";
  let api = "";

  if (paths.length >= 2) {
    area = paths[1].toLowerCase();
  }

  const href = URI(getUrl("/docs")).addSearch("id", id);

  if (area === "restapi" && paths.length >= 4) {
    api = paths[3].toLowerCase();
  }

  return {
    id: id,
    product,
    api: api,
    area: area,
    href: href.toString(),
    remoteUrl: url,
    isTryItOut: false,
    isDefault: id === defaultDocPage,
  };
}

function getAPI<TSearchParams>(location: Location<TSearchParams>): IDocLocation {
  const { pathname } = location;
  let paths = pathname.split("/");
  const repo = paths[1];
  const product = repo.startsWith("qmlativ") ? Product.qmlativ : Product.sms;
  let api = "general";
  if (paths.length >= 3) {
    api = paths[2].toLowerCase();
    if (api.endsWith(".html")) {
      api = api.substring(0, api.length - 5);
    }
  }

  return {
    id: "",
    product,
    href: `/try-it-out/${product}/${api}`,
    area: "restapi",
    api: api,
    remoteUrl: "",
    isTryItOut: true,
    isDefault: false,
  };
}

function getLocationInfo<TSearchParams>(location: Location<TSearchParams>): ILocation {
  let doc: IDocLocation | undefined = undefined;
  let tab: string = "/default";

  if (location.pathname && location.pathname.startsWith("/try-it-out")) {
    doc = getAPI(location);
    tab = "/docs";
  } else if (location.pathname && location.pathname.startsWith("/docs")) {
    doc = getDocsInfo(location);
    tab = "/docs";
  } else {
    if (
      location.pathname.startsWith("/support") ||
      location.pathname.startsWith("/submit-sc") ||
      location.pathname.startsWith("/submit-sr") ||
      location.pathname.startsWith("/list-sc") ||
      location.pathname.startsWith("/test-credentials")
    ) {
      tab = "/support";
    } else if (location.pathname.startsWith("/partnership")) {
      tab = "/partnership";
    }
  }

  let info: ILocation = {
    path: location.pathname,
    tab: tab,
    doc: doc,
  };
  // uncomment for very useful context information
  // console.log("LOCATION", JSON.stringify(info, null, 2));
  return info;
}

export default getLocationInfo;
